import { DataEntry } from 'models/data';
import { Tile } from 'components/Layout/PageContainer';
import { Attribute, Measure } from 'services/model/model.service';
import { BarChartRenderer } from 'components/chart/renderer/BarChartRenderer';
import { MetricsChart } from 'components/MetricsChart';
import { PerformanceGapList } from 'pages/Benchmarking/common/performance-gap/PerformanceGapList';
import { useContext, useState } from 'react';
import { MeasureUnitCode } from 'components/measure-unit/MeasureUnitCode';
import { BarChartConfig } from 'components/chart/data/BarChartConfig';
import { ChartDataArray } from 'components/chart/data/ChartDataArray';
import { ChartDataType } from 'components/chart/data/ChartDataType';
import { ChartData } from 'components/chart/data/ChartData';
import { chartColors } from 'styles/configs/chartColors';
import { TenantConfigContext } from 'context/TenantConfigContext';
import { useIntl } from 'react-intl';
import { Pagination, ToggleButtonGroup } from '@mui/material';
import {
  calculatePerformanceGap,
  extractPeerGrowthValue,
  extractPeerValue,
  extractTenantGrowthValue,
  extractTenantValue,
} from 'pages/Benchmarking/common/topOpportunitiesValueExtractors';
import { MetricsSectionTitle } from 'components/MetricsSectionTitle';
import { StyledToggleButton } from 'components/StyledToggleButton';
import { findIconForCategory } from 'pages/insights-pro/common/icons/CategoryIcons';
import { MetricsLoadingSpinner } from 'components/MetricsLoadingSpinner';
import { NoOpportunityError } from 'pages/Benchmarking/components/NoOpportunityError';
import { BenchmarkEmptyState } from 'components/BenchmarkEmptyState';
import { BenchmarkEmptyStateType } from 'components/benchmarkEmptyStateUtils';
import { getBenchmarkEmptyStateType } from 'pages/Benchmarking/common/performance-gap/getBenchmarkEmptyStateType';
import { EmptyState } from 'components/EmptyState';

type PerformanceSortType = 'all' | 'over' | 'under';

interface PerformanceGapDisplayProps {
  dataSet: DataEntry[];
  improvementMeasure: Measure;
  groupingAttribute: Attribute;
  interpretation: number;
  drilldown?: boolean;
  benchmark: string;
  isPending: boolean;
  opportunitiesTestId?: string;
  chartTileTestId?: string;
  chartTestId?: string;
}

interface SortOption {
  value: PerformanceSortType;
  id: string;
}

export function PerformanceGapEmptyState({ type, testId = '' }: { type: BenchmarkEmptyStateType; testId?: string }) {
  return <BenchmarkEmptyState type={type} testId={testId} />;
}

export function PerformanceGapDisplay({
  dataSet,
  improvementMeasure,
  groupingAttribute,
  interpretation,
  drilldown = false,
  benchmark,
  isPending,
  opportunitiesTestId = undefined,
  chartTileTestId = undefined,
  chartTestId = undefined,
}: PerformanceGapDisplayProps) {
  const { name } = useContext(TenantConfigContext);
  const intl = useIntl();

  const [pageSize] = useState(5);
  const [currentPage, setCurrentPage] = useState(1);
  const [sort, setSort] = useState<PerformanceSortType>('all');

  const handleSortSelect = (_event: React.MouseEvent<HTMLElement>, newSort: PerformanceSortType) => {
    if (!newSort || newSort === sort) {
      return;
    }

    setSort(newSort);
  };

  const sortOptions: SortOption[] = [
    {
      value: 'all',
      id: 'benchmarking.benchmarks.opportunities.sort.all',
    },
    {
      value: 'under',
      id: 'benchmarking.benchmarks.opportunities.sort.under',
    },
    {
      value: 'over',
      id: 'benchmarking.benchmarks.opportunities.sort.over',
    },
  ];

  const getComparisonValueFunc = () => {
    switch (improvementMeasure.code) {
      case 'ordGmvVal':
        return (entry: DataEntry) =>
          calculatePerformanceGap({ improvementMeasure, currentItem: entry, selectedBenchmark: benchmark });
      default:
        return (entry: DataEntry) => improvementMeasure.getValue(entry?.values);
    }
  };

  const getPerformanceGapFunc = () => {
    switch (improvementMeasure.code) {
      case 'ordGmvVal':
        return (entry: DataEntry) =>
          calculatePerformanceGap({ improvementMeasure, currentItem: entry, selectedBenchmark: benchmark });
      default:
        return (entry: DataEntry) =>
          extractTenantValue(improvementMeasure, entry) - extractPeerValue(improvementMeasure, entry, benchmark);
    }
  };

  const paginationDataSet = dataSet
    .filter(entry => !!entry.benchmarks && !!entry.benchmarks[benchmark])
    .filter(entry => {
      const performanceGapFunc = getPerformanceGapFunc();
      const performanceGap = performanceGapFunc(entry);
      switch (sort) {
        case 'over':
          return interpretation > 0 ? performanceGap > 0 : performanceGap < 0;
        case 'under':
          return interpretation > 0 ? performanceGap < 0 : performanceGap > 0;
        default:
          return true;
      }
    });
  const pageCount = Math.ceil(paginationDataSet.length / pageSize);

  const paginatedList = paginationDataSet
    .sort((a, b) => {
      const valueExtractor = getComparisonValueFunc();
      return valueExtractor(b) - valueExtractor(a);
    })
    .slice((currentPage - 1) * pageSize, currentPage * pageSize);

  const benchmarkEmptyStateType = getBenchmarkEmptyStateType(dataSet, improvementMeasure, benchmark);
  const benchmarkEmptyState = benchmarkEmptyStateType ? <BenchmarkEmptyState type={benchmarkEmptyStateType} /> : null;
  const renderEmptyState = () => benchmarkEmptyState ?? <NoOpportunityError />;

  if (!isPending && benchmarkEmptyState) {
    return <PerformanceGapEmptyState type={benchmarkEmptyStateType!} testId={chartTileTestId} />;
  }

  const createBarChartConfig = (
    measure: { name: string; unit: MeasureUnitCode },
    valuesExtract: (
      entry:
        | {
            values: Record<string, number>;
            compareValues: Record<string, number>;
          }
        | undefined
    ) => number
  ) => {
    const reversedList = [...paginatedList].reverse();

    const chartConfig = new BarChartConfig();
    chartConfig.value = {
      label: measure.name,
      unit: measure.unit,
    };

    chartConfig.legendConfig = {
      hide: false,
    };

    const primaryArray = new ChartDataArray(
      ChartDataType.primary,
      name,
      reversedList.map(entry => new ChartData(groupingAttribute.getValue(entry.attributes), valuesExtract(entry)))
    );
    primaryArray.color = chartColors.TENANT;

    const secondaryArray = new ChartDataArray(
      ChartDataType.secondary,
      intl.formatMessage({
        id: `benchmarking.benchmarks.${benchmark}`,
      }),
      reversedList.map(
        entry =>
          new ChartData(groupingAttribute.getValue(entry.attributes), valuesExtract(entry.benchmarks![benchmark]))
      )
    );
    secondaryArray.color = chartColors.PEER;

    chartConfig.data = [primaryArray, secondaryArray];

    return chartConfig;
  };

  const getChartRenderer = () => {
    switch (improvementMeasure.code) {
      case 'ordGmvVal':
        return new BarChartRenderer(
          createBarChartConfig(
            { name: intl.formatMessage({ id: 'benchmarking.tooltip.growth' }), unit: MeasureUnitCode.PERCENT },
            (
              entry:
                | {
                    values: Record<string, number>;
                    compareValues: Record<string, number>;
                  }
                | undefined
            ) => improvementMeasure.getValue(entry?.values) / improvementMeasure.getValue(entry?.compareValues) - 1
          )
        );
      case 'cncQtyRate':
      case 'retQtyRateExpOD':
        return new BarChartRenderer(
          createBarChartConfig(
            { name: improvementMeasure.metric.name, unit: improvementMeasure.metric.unit },
            (
              entry:
                | {
                    values: Record<string, number>;
                    compareValues: Record<string, number>;
                  }
                | undefined
            ) => improvementMeasure.getValue(entry?.values)
          )
        );
      default:
        return null;
    }
  };

  const chartRenderer = paginatedList.length > 0 ? getChartRenderer() : null;

  return (
    <Tile testId={chartTileTestId}>
      {isPending ? (
        <MetricsLoadingSpinner />
      ) : (
        <>
          <div className="pb-4">
            <ToggleButtonGroup value={sort} exclusive onChange={handleSortSelect}>
              {sortOptions.map(option => (
                <StyledToggleButton
                  key={option.value || 'none'}
                  size="small"
                  className="whitespace-nowrap text-xs"
                  value={option.value}
                  sx={{ color: '#1A1A1A' }}
                >
                  {intl.formatMessage({ id: option.id })}
                </StyledToggleButton>
              ))}
            </ToggleButtonGroup>
          </div>
          <div className="flex gap-4">
            <div className="flex-1">
              <MetricsSectionTitle
                additionalTitle={intl.formatMessage({
                  id: `benchmarking.titles.additional.by.${improvementMeasure.code}.gap`,
                })}
              >
                {intl.formatMessage({ id: `benchmarking.titles.list.${groupingAttribute.code}` })}
              </MetricsSectionTitle>
              {paginatedList.length === 0 ? (
                <EmptyState />
              ) : (
                <PerformanceGapList
                  selectedBenchmark={benchmark}
                  improvementMeasure={improvementMeasure}
                  groupingAttribute={groupingAttribute}
                  dataset={paginatedList}
                  interpretation={interpretation}
                  calculateTenantValue={improvementMeasure.code === 'ordGmvVal' ? extractTenantGrowthValue : undefined}
                  calculatePeerValue={improvementMeasure.code === 'ordGmvVal' ? extractPeerGrowthValue : undefined}
                  calculateGap={improvementMeasure.code === 'ordGmvVal' ? calculatePerformanceGap : undefined}
                  getIcon={
                    groupingAttribute.code === 'prdCategory'
                      ? entry => findIconForCategory(entry.attributes.prdCategory, 24)
                      : undefined
                  }
                  testId={opportunitiesTestId}
                  drilldown={drilldown}
                />
              )}
            </div>
            <div className="w-px bg-snow-200 mx-2" style={{ minHeight: 400 }} />
            <div className="flex-1">
              <MetricsSectionTitle
                additionalTitle={intl.formatMessage({
                  id: `benchmarking.titles.chart.additional.${groupingAttribute.code}`,
                })}
              >
                {intl.formatMessage({ id: `benchmarking.titles.chart.${improvementMeasure.code}` })}
              </MetricsSectionTitle>
              {paginatedList.length !== 0 && chartRenderer ? (
                <MetricsChart renderer={chartRenderer} height="h-[400px]" testId={chartTestId} emptyState={null} />
              ) : (
                renderEmptyState()
              )}
            </div>
          </div>
          {pageCount > 1 && (
            <div className="flex justify-center">
              <Pagination
                className="mt-4"
                size="small"
                count={pageCount}
                page={currentPage}
                onChange={(_event, page) => setCurrentPage(page)}
              />
            </div>
          )}
        </>
      )}
    </Tile>
  );
}
